import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Calendar, AlertTriangle, Clock, CheckCircle2, FileClock, ChevronRight, Copy, Loader2 } from 'lucide-react';

/**
 * Receivables Tracker — shows every scheduled payment across contract-based
 * divisions, with status pills (Received / PDC / Awaiting PO / Overdue) and
 * quick WhatsApp-copy reminders.
 */

const STATUS_META = {
  overdue:     { label: 'Overdue',     bg: '#FEF2F2', fg: '#991B1B', icon: AlertTriangle },
  pdc:         { label: 'PDC',         bg: '#DBEAFE', fg: '#1E3A8A', icon: Calendar },
  awaiting_po: { label: 'Awaiting PO', bg: '#FEF3C7', fg: '#92400E', icon: FileClock },
  scheduled:   { label: 'Scheduled',   bg: '#F3E8FF', fg: '#6B21A8', icon: Clock },
  received:    { label: 'Received',    bg: '#ECFDF5', fg: '#065F46', icon: CheckCircle2 },
};

const DIV_LEADER = {
  'cs': 'Nisha',
  'cc-clubs': 'Nisha / Lohita',
  'cc-ops': 'Samar',
  'lc': 'Srikanth',
  'me': 'Dhiraj',
  'tdb': 'Pratap',
};

function fmtCr(v) {
  const n = Number(v) || 0;
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)}L`;
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
}

export default function ReceivablesTracker() {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState('');
  const [divFilter, setDivFilter] = useState('all');
  const [markOpen, setMarkOpen] = useState(null); // the row being marked received
  const [markAmount, setMarkAmount] = useState('');
  const [markDate, setMarkDate] = useState('');
  const [markInvoice, setMarkInvoice] = useState('');
  const [marking, setMarking] = useState(false);
  const [markError, setMarkError] = useState('');

  const today = () => new Date().toISOString().slice(0, 10);

  const load = async () => {
    try {
      const [s, u] = await Promise.all([
        api.get('/receivables/summary'),
        api.get('/receivables/upcoming?days=365'),
      ]);
      setSummary(s.data);
      setItems(u.data.items || []);
    } catch {
      // non-authorised — widget hides
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading || !summary) return null;
  if (!items.length && summary.received.count === 0) return null; // nothing to show

  const copyReminder = async (row) => {
    const leader = DIV_LEADER[row.division_id] || 'team';
    let msg;
    if (row.status === 'overdue') {
      msg = `${leader} — ${row.client_name} payment of ${fmtCr(row.amount)} was due ${row.due_date} and not recorded yet. Please check status (cheque received? deposited? bounced?) and log it in ScaleBoard → ${row.division_id.toUpperCase()} → Monthly Billing.`;
    } else if (row.status === 'pdc') {
      msg = `${leader} — heads-up: ${row.client_name} PDC of ${fmtCr(row.amount)} due ${row.due_date}. Once cheque clears, log in ScaleBoard → Monthly Billing → Mark Received.`;
    } else if (row.status === 'awaiting_po') {
      msg = `${leader} — follow up with ${row.client_name} on their PO (${fmtCr(row.amount)}, expected ~${row.due_date}). Any updates on timing? Log once received.`;
    } else {
      msg = `${leader} — ${row.client_name} ${fmtCr(row.amount)} due ${row.due_date}. Record in ScaleBoard once received.`;
    }
    try {
      await navigator.clipboard.writeText(msg);
      setCopied(`${row.contract_id}-${row.schedule_index}`);
      setTimeout(() => setCopied(''), 1800);
    } catch { /* ignore */ }
  };

  const openMark = (row) => {
    setMarkOpen(row);
    setMarkAmount(String(row.amount));
    setMarkDate(today());
    setMarkInvoice('');
    setMarkError('');
  };

  const submitMark = async () => {
    if (!markOpen) return;
    const amt = parseFloat(markAmount);
    if (!amt || amt <= 0) {
      setMarkError('Enter a valid amount');
      return;
    }
    setMarking(true);
    setMarkError('');
    try {
      await api.post(`/contracts/${markOpen.contract_id}/payments/${markOpen.schedule_index}/mark-received`, {
        received_date: markDate,
        amount: amt,
        invoice_ref: markInvoice,
      });
      setMarkOpen(null);
      await load();
    } catch (e) {
      setMarkError(e.response?.data?.detail || 'Failed to record payment');
    } finally {
      setMarking(false);
    }
  };

  const headline = summary.next_30_days.count;
  const overdue = summary.overdue.count;

  return (
    <div className="rounded-xl border bg-white" style={{ borderColor: '#E5E1DC' }} data-testid="receivables-tracker">
      {/* Header */}
      <div className="p-4 flex items-start justify-between flex-wrap gap-3 cursor-pointer hover:bg-slate-50" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: overdue > 0 ? '#FEF2F2' : '#ECFDF5' }}>
            {overdue > 0
              ? <AlertTriangle size={17} style={{ color: '#991B1B' }} />
              : <CheckCircle2 size={17} style={{ color: '#065F46' }} />}
          </div>
          <div>
            <h3 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>Active Receivables</h3>
            <p className="text-xs" style={{ color: '#6B7280' }}>
              {overdue > 0 && (
                <span className="font-semibold" style={{ color: '#991B1B' }}>
                  {overdue} overdue · {fmtCr(summary.overdue.amount)}  ·  {' '}
                </span>
              )}
              {headline > 0
                ? <>Next 30 days: <strong>{headline} items · {fmtCr(summary.next_30_days.amount)}</strong></>
                : 'All scheduled payments up to date'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {['received', 'pdc', 'awaiting_po', 'overdue'].map(k => {
            const s = summary[k];
            if (!s.count) return null;
            const m = STATUS_META[k];
            const Icon = m.icon;
            return (
              <div key={k} className="inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full" style={{ backgroundColor: m.bg, color: m.fg }}>
                <Icon size={11} />
                <span className="font-semibold">{s.count}</span>
                <span className="hidden sm:inline">· {fmtCr(s.amount)}</span>
              </div>
            );
          })}
          <ChevronRight size={14} style={{ color: '#9CA3AF', transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />
        </div>
      </div>

      {/* Expanded list */}
      {expanded && (
        <div className="border-t" style={{ borderColor: '#F3F4F6' }} data-testid="receivables-list">
          {/* Division filter */}
          <div className="px-3 py-2 flex items-center gap-1.5 flex-wrap text-[11px]" style={{ borderBottom: '1px solid #F3F4F6', backgroundColor: '#FAFAF9' }}>
            <span style={{ color: '#9CA3AF' }}>Filter:</span>
            {['all', ...Array.from(new Set(items.map(i => i.division_id)))].map(d => (
              <button
                key={d}
                onClick={(e) => { e.stopPropagation(); setDivFilter(d); }}
                className="px-2 py-0.5 rounded-full font-semibold transition-colors"
                style={{
                  backgroundColor: divFilter === d ? '#1B2A4A' : 'white',
                  color: divFilter === d ? 'white' : '#6B7280',
                  border: '1px solid ' + (divFilter === d ? '#1B2A4A' : '#E5E1DC'),
                }}
                data-testid={`filter-${d}`}
              >
                {d === 'all' ? `All (${items.length})` : `${d} (${items.filter(i => i.division_id === d).length})`}
              </button>
            ))}
          </div>
          <div className="overflow-x-auto">
          {items.length === 0 ? (
            <p className="p-4 text-center text-xs" style={{ color: '#9CA3AF' }}>
              No scheduled payments upcoming.
            </p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr style={{ backgroundColor: '#FAF9F7', color: '#9CA3AF' }}>
                  <th className="text-left px-3 py-1.5 font-semibold">Due</th>
                  <th className="text-left px-3 py-1.5 font-semibold">Client</th>
                  <th className="text-left px-3 py-1.5 font-semibold">Division</th>
                  <th className="text-right px-3 py-1.5 font-semibold">Amount</th>
                  <th className="text-left px-3 py-1.5 font-semibold">Status</th>
                  <th className="text-right px-3 py-1.5 font-semibold whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
                {items.filter(r => divFilter === 'all' || r.division_id === divFilter).slice(0, 100).map(r => {
                  const m = STATUS_META[r.status] || STATUS_META.scheduled;
                  const Icon = m.icon;
                  const rowKey = `${r.contract_id}-${r.schedule_index}`;
                  const daysText = r.days_to_due != null
                    ? (r.days_to_due < 0 ? `${Math.abs(r.days_to_due)}d overdue` : r.days_to_due === 0 ? 'today' : `in ${r.days_to_due}d`)
                    : '';
                  return (
                    <tr key={rowKey} data-testid={`receivable-${rowKey}`} className="hover:bg-slate-50">
                      <td className="px-3 py-2" style={{ color: '#1B2A4A' }}>
                        {r.due_date}
                        {daysText && (
                          <span className="ml-1.5 text-[10px]" style={{ color: r.status === 'overdue' ? '#991B1B' : '#9CA3AF' }}>
                            {daysText}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2" style={{ color: '#1B2A4A' }}>
                        <div className="font-medium">{r.client_name}</div>
                        {r.notes && <div className="text-[10px]" style={{ color: '#9CA3AF' }}>{r.notes}</div>}
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-[10px] uppercase px-1.5 py-0.5 rounded font-semibold" style={{ backgroundColor: '#F3F4F6', color: '#6B7280' }}>
                          {r.division_id}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-semibold" style={{ color: '#1B2A4A' }}>
                        {fmtCr(r.amount)}
                      </td>
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-semibold" style={{ backgroundColor: m.bg, color: m.fg }}>
                          <Icon size={10} /> {m.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button
                          onClick={(e) => { e.stopPropagation(); openMark(r); }}
                          className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-md font-semibold text-white mr-1"
                          style={{ backgroundColor: '#2A7B88' }}
                          title="Mark as received — moves this payment to realised"
                          data-testid={`mark-${rowKey}`}
                        >
                          <CheckCircle2 size={11} /> Mark Received
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); copyReminder(r); }}
                          className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded hover:bg-slate-100"
                          style={{ color: '#2A7B88' }}
                          title="Copy WhatsApp reminder to clipboard"
                          data-testid={`copy-${rowKey}`}
                        >
                          {copied === rowKey ? <><CheckCircle2 size={11} /> Copied</> : <><Copy size={11} /> WA</>}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          </div>
        </div>
      )}

      {/* Mark Received modal */}
      {markOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }} onClick={() => !marking && setMarkOpen(null)} data-testid="mark-received-modal">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>Mark Payment Received</h3>
                <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>{markOpen.client_name} · due {markOpen.due_date}</p>
              </div>
            </div>
            <div className="space-y-3">
              <label className="block">
                <span className="text-[11px] font-semibold uppercase" style={{ color: '#9CA3AF' }}>Amount received</span>
                <input
                  type="number"
                  value={markAmount}
                  onChange={(e) => setMarkAmount(e.target.value)}
                  className="w-full mt-1 px-2 py-1.5 text-sm border rounded"
                  style={{ borderColor: '#E5E1DC' }}
                  data-testid="mark-amount"
                />
                <span className="text-[10px]" style={{ color: '#9CA3AF' }}>Pre-filled from scheduled amount · in ₹</span>
              </label>
              <label className="block">
                <span className="text-[11px] font-semibold uppercase" style={{ color: '#9CA3AF' }}>Received on</span>
                <input
                  type="date"
                  value={markDate}
                  onChange={(e) => setMarkDate(e.target.value)}
                  className="w-full mt-1 px-2 py-1.5 text-sm border rounded"
                  style={{ borderColor: '#E5E1DC' }}
                  data-testid="mark-date"
                />
              </label>
              <label className="block">
                <span className="text-[11px] font-semibold uppercase" style={{ color: '#9CA3AF' }}>Invoice / cheque ref (optional)</span>
                <input
                  type="text"
                  value={markInvoice}
                  onChange={(e) => setMarkInvoice(e.target.value)}
                  placeholder="INV-2026-123 / CHQ #452019"
                  className="w-full mt-1 px-2 py-1.5 text-sm border rounded"
                  style={{ borderColor: '#E5E1DC' }}
                  data-testid="mark-invoice"
                />
              </label>
              {markError && <p className="text-xs" style={{ color: '#991B1B' }} data-testid="mark-error">{markError}</p>}
            </div>
            <div className="flex items-center justify-end gap-2 mt-5">
              <button onClick={() => setMarkOpen(null)} disabled={marking} className="text-xs px-3 py-1.5 rounded" style={{ color: '#6B7280' }} data-testid="mark-cancel">
                Cancel
              </button>
              <button onClick={submitMark} disabled={marking} className="text-xs font-semibold px-4 py-1.5 rounded text-white inline-flex items-center gap-1" style={{ backgroundColor: '#2A7B88' }} data-testid="mark-confirm">
                {marking ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
